import {
	Box,
	ButtonBase,
	Divider,
	Stack,
	Typography,
	useTheme,
} from "@mui/material";
import { Plus } from "lucide-react";
import type { FC } from "react";
import { useNavigate } from "react-router-dom";
import {
	EXAMPLE_GOAL_CATEGORIES,
	type ExampleGoalCategory,
	GOAL_CATEGORY_CONFIG,
} from "web_myplann/Goals/Types/GoalTypes";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { getRoute } from "web_myplann/Infrastructure/Utils/UrlUtils";
import { IconBox } from "web_myplann/Shared/Components/IconBox";

const EXAMPLE_RESOURCES = {
	Pension: Resources.Goals.EmptyState.Examples.Retirement,
	House: Resources.Goals.EmptyState.Examples.Housing,
	Vacation: Resources.Goals.EmptyState.Examples.Vacation,
} as const satisfies Record<
	ExampleGoalCategory,
	{ name: unknown; reason: unknown }
>;

const ICON_COLOR_REF = {
	Pension: { scale: "orange", shade: 400 },
	House: { scale: "blue", shade: 400 },
	Vacation: { scale: "blue", shade: 300 },
} as const satisfies Record<
	ExampleGoalCategory,
	{ scale: "blue" | "orange"; shade: 300 | 400 }
>;

export const ExampleGoalList: FC = () => {
	const { palette, iconSize } = useTheme();
	const { t } = useResource();
	const navigate = useNavigate();

	const handleSelect = (category: ExampleGoalCategory) => {
		navigate(`${getRoute(Routing.Goals.GoalFormCreate)}?category=${category}`);
	};

	return (
		<Stack bgcolor={palette.background.paper}>
			{EXAMPLE_GOAL_CATEGORIES.map((category, categoryIndex) => {
				const config = GOAL_CATEGORY_CONFIG[category];
				const Icon = config.icon;
				const resource = EXAMPLE_RESOURCES[category];
				const { scale, shade } = ICON_COLOR_REF[category];

				return (
					<Box key={category}>
						<ButtonBase
							onClick={() => handleSelect(category)}
							sx={{ width: "100%", textAlign: "left" }}
						>
							<Stack
								direction="row"
								alignItems="center"
								gap={1.4}
								px={1.6}
								py={1.4}
								width="100%"
							>
								<IconBox
									bg={palette.grey[100]}
									border={`1px dashed ${palette.grey[300]}`}
								>
									<Icon size={iconSize.md} color={palette[scale][shade]} />
								</IconBox>
								<Stack flex={1} minWidth={0}>
									<Typography variant="bodyMedium" color={palette.grey[500]}>
										{t(resource.name)}
									</Typography>
									<Typography variant="hint" mt={0.2}>
										{t(resource.reason)}
									</Typography>
								</Stack>
								<Stack
									direction="row"
									alignItems="center"
									gap={0.4}
									flexShrink={0}
								>
									<Typography variant="button" color={palette.blue[600]}>
										{t(Resources.Goals.EmptyState.addLabel)}
									</Typography>
									<Plus size={14} color={palette.blue[600]} />
								</Stack>
							</Stack>
						</ButtonBase>
						{categoryIndex < EXAMPLE_GOAL_CATEGORIES.length - 1 && (
							<Divider sx={{ borderColor: "surface.border" }} />
						)}
					</Box>
				);
			})}
		</Stack>
	);
};
